import { Alert, Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { RocketLaunchIcon } from '@phosphor-icons/react';

import { PRIVACY_TIERS, type PrivacyTier } from '@/lib/onboarding/privacy-tiers';
import type { SiteThemePreset } from '@/lib/site-styles/theme-presets';
import type { SiteTemplate } from '@/lib/templates/types';

interface ReviewPublishStepProps {
  unitName: string;
  template: SiteTemplate | null;
  themePreset: SiteThemePreset;
  privacyTier: PrivacyTier;
  publishing: boolean;
  error: string | null;
  onPublish: () => void;
}

// Spec section 4, Step 7 — "Immediate publish to a free subdomain with
// SSL. No payment, no domain, no DNS." Publishing here means: create the
// template's pages/blocks, then actually publish each one (through the
// same youth-PII preflight every other publish goes through — see
// use-onboarding-publish.ts) — today's inline template gallery only ever
// created draft pages, so this is also fixing that gap.
export function ReviewPublishStep({ unitName, template, themePreset, privacyTier, publishing, error, onPublish }: ReviewPublishStepProps) {
  const tier = PRIVACY_TIERS.find((option) => option.id === privacyTier);

  return (
    <Stack spacing={2.5}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Ready to publish
      </Typography>
      <Typography color="text.secondary">
        {unitName}&apos;s site will go live at your free Scouting Ops subdomain — no domain or payment needed.
      </Typography>

      <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 2.5 }}>
        <Stack spacing={1.5} divider={<Divider />}>
          <SummaryRow label="Template" value={template?.name ?? 'None selected'} />
          <SummaryRow label="Look" value={themePreset.name} />
          <SummaryRow label="Visibility" value={tier?.name ?? 'Open front door'} />
        </Stack>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Button
        variant="contained"
        size="large"
        startIcon={publishing ? <CircularProgress size={16} color="inherit" /> : <RocketLaunchIcon size={18} />}
        disabled={publishing || !template}
        onClick={onPublish}
      >
        {publishing ? 'Publishing…' : 'Publish my site'}
      </Button>
    </Stack>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
      <Typography color="text.secondary">{label}</Typography>
      <Typography sx={{ fontWeight: 600 }}>{value}</Typography>
    </Stack>
  );
}
